import { useEffect, useState } from "react";
import CoaCard, { CoaCardProps } from "./components/CoaCard";
import { getCoaSelectionKey } from './components/CoaCard/CoaCard';
import CoaResultsButton from "./components/CoaResultsButton";
import CoaSelectionTab from "./components/CoaSelectionTabBar";


/*

TODO: No ticket created

This is temporarily to be presented as a page. When we are ready to integrate this 
into the Lab results page, we can convert this to a 'CoaResultsSection' component.
*/
function CoaResultsPage({ activeCoas, loading = false, error = null, onRetry }: {
    activeCoas: CoaCardProps[];
    loading?: boolean;
    error?: string | null;
    onRetry?: () => void;
}) {


    const multipleCoasExist = activeCoas.length > 1;


    const [selectedCoa, setSelectedCoa] = useState<CoaCardProps | null>(
        activeCoas.length > 0 ? activeCoas[0] : null
    );
    const [selectedFileIndex, setSelectedFileIndex] = useState(0);
    

    useEffect(() => {
        // Keep the selection only while its record survives the new results; a stale COA from a
        // previously viewed package must never outlive the data it came from.
        setSelectedCoa((current) => (
            activeCoas.find((coa) => current && getCoaSelectionKey(coa) === getCoaSelectionKey(current)) ?? activeCoas[0] ?? null
        ));
    }, [activeCoas]);

    useEffect(() => {
        setSelectedFileIndex(0);
    }, [selectedCoa?.selectionKey, selectedCoa?.id]);

    useEffect(() => {
        if (selectedFileIndex >= (selectedCoa?.files.length ?? 0)) {
            setSelectedFileIndex(0);
        }
    }, [selectedCoa, selectedFileIndex]);

    const selectedFile = selectedCoa?.files[selectedFileIndex] || null;
    // Lab identity belongs to the selected document, so it must not fall back to the record.
    const displayedCoa = selectedCoa && selectedFile
        ? {
            ...selectedCoa,
            date: selectedFile.testPerformedDate || selectedCoa.date,
            labStatus: selectedFile.labTestingStateName || 'NotSubmitted',
            testerName: selectedFile.testerName,
            labLicenseNumber: selectedFile.labLicenseNumber,
        }
        : selectedCoa;

    return (
        <div className="coa-results-wrapper" data-testid="coa-results">
            <h2 className="coa-title" data-testid="coa-results-title">COA Overview</h2>
            {
                multipleCoasExist && (
                    <div data-testid="coa-results-tabs">
                        <CoaSelectionTab selectedCoa={selectedCoa} activeCoas={activeCoas} onSelectCoa={setSelectedCoa} />
                    </div>
                )
            }
            {selectedFile ? (
                <>
                    {selectedCoa.files.length > 1 && (
                        <div className="coa-selection-tabbar" data-testid="coa-results-file-tabs">
                            {selectedCoa.files.map((file, index) => (
                                <button
                                    className={`coa-selection-tab ${index === selectedFileIndex ? 'selected' : ''}`}
                                    data-testid={`coa-results-file-tab-${index + 1}`}
                                    key={`${file.url}-${index}`}
                                    onClick={() => setSelectedFileIndex(index)}
                                    type="button"
                                    aria-pressed={index === selectedFileIndex}
                                >
                                    <span className="coa-selection-tab-label">COA {index + 1}</span>
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="coa-results-file" data-testid={`coa-results-file-${selectedFileIndex + 1}`}>
                        <CoaResultsButton coaUrl={selectedFile.url} testId={`coa-results-view-link-${selectedFileIndex + 1}`} />
                        {selectedFile.fileName && (
                            <p className="muted" data-testid={`coa-results-file-name-${selectedFileIndex + 1}`}>{selectedFile.fileName}</p>
                        )}
                    </div>
                </>
            ) : error ? (
                <div className="coa-results-error" data-testid="coa-results-error">
                    <p className="muted" data-testid="coa-results-error-message">{error}</p>
                    {onRetry && (
                        <button
                            className="coa-results-retry"
                            data-testid="coa-results-retry"
                            onClick={onRetry}
                            type="button"
                        >
                            Retry
                        </button>
                    )}
                </div>
            ) : loading ? (
                <p className="muted" data-testid="coa-results-loading">Loading COA results…</p>
            ) : !selectedCoa ? (
                <p className="muted" data-testid="coa-results-empty">No COA results are available.</p>
            ) : null}
            <div data-testid="coa-results-card">
                <CoaCard coa={displayedCoa} />
            </div>
        </div>
    );
}

export default CoaResultsPage;
